import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "./Button";
import { cn } from "@/lib/utils";
import wordmark from "@/assets/fenny-wordmark-black.svg";
import symbol from "@/assets/fenny-symbol-black.svg";
import { type MegaPanel, type MegaItem } from "@/content/nav";
import { comingSoonAreas } from "@/content/service-areas";

import { useLocalizedNav } from "@/i18n/useLocalizedNav";
import { useLocalizedHref } from "@/i18n/useLocalizedHref";
import { detectLocale } from "@/i18n/routeMap";
import { track } from "@/lib/analytics";
import { MobileMenu } from "./MobileMenu";
import { LanguageSwitcher } from "./LanguageSwitcher";

function MegaItemLink({ item, onNav }: { item: MegaItem; onNav: () => void }) {
  return (
    <Link
      to={item.to as string}
      params={item.params as never}
      onClick={onNav}
      className="group block rounded-lg px-3 py-2 transition-colors hover:bg-black/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/20"
    >
      <div className="flex items-center gap-1.5">
        <span className="text-[13.5px] font-semibold leading-tight text-black">
          {item.label}
        </span>
        <ArrowRight className="h-3 w-3 shrink-0 -translate-x-1 text-black/40 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100" />
      </div>
      {item.desc && (
        <div className="mt-0.5 line-clamp-2 text-[11.5px] leading-snug text-black/50">
          {item.desc}
        </div>
      )}
    </Link>
  );
}

function MegaPanelContent({
  panel,
  onNav,
}: {
  panel: MegaPanel;
  onNav: () => void;
}) {
  return (
    <div className="flex max-h-[calc(100vh-7.5rem)] items-stretch gap-0 overflow-y-auto p-5 xl:p-6">
      {panel.columns.map((col, i) => (
        <div
          key={i}
          className={cn(
            "min-w-0 flex-1 px-2",
            i > 0 && "border-l border-black/[0.06]",
          )}
        >
          {col.eyebrow.trim() && (
            <div className="mb-1.5 px-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-black/40">
              {col.eyebrow}
            </div>
          )}
          <div className="flex flex-col">
            {col.items.map((item) => (
              <MegaItemLink
                key={`${item.to}-${item.params?.slug ?? item.label}`}
                item={item}
                onNav={onNav}
              />
            ))}
          </div>
        </div>
      ))}
      {panel.featured && (
        <div className="ml-3 w-[268px] shrink-0 xl:w-[300px]">
          <Link
            to={panel.featured.to as string}
            params={panel.featured.params as never}
            onClick={onNav}
            className="group flex flex-col rounded-2xl bg-black p-5 text-white transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/30"
          >
            <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
              {panel.featured.eyebrow}
            </div>
            <div className="mt-2.5 text-[17px] font-semibold leading-snug">
              {panel.featured.title}
            </div>
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-white/70">
              {panel.featured.body}
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-white/90">
              {panel.featured.cta}
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </div>
          </Link>
        </div>
      )}
    </div>
  );
}


export function SiteHeader() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const closeTimer = useRef<number | null>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isEn = detectLocale(pathname) === "en";
  void isEn;
  const loc = useLocalizedHref();
  const nav = useLocalizedNav();
  const contactTo = loc("/kontakt");

  useEffect(() => {
    let raf = 0;
    const compute = () => {
      raf = 0;
      const y = window.scrollY;
      setScrolled(y > 12);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pathname]);

  useEffect(() => {
    setOpen(false);
    setOpenMenu(null);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Close mega on Escape and on long scroll
  useEffect(() => {
    if (!openMenu) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpenMenu(null);
    let startY = window.scrollY;
    const onScroll = () => {
      if (Math.abs(window.scrollY - startY) > 80) setOpenMenu(null);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll);
    };
  }, [openMenu]);

  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenMenu(null), 160);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  // Mega-panelernas innehåll är hundratals länkar. Vi renderar dem först när
  // menyn faktiskt öppnats — HTML-vikten per sidvisning sjunker rejält och
  // länkarna finns kvar i footern och på /alla-sidor för crawlers.
  const [mountedMenus, setMountedMenus] = useState<string[]>([]);

  const handleOpen = (label: string) => {
    cancelClose();
    setMountedMenus((prev) => (prev.includes(label) ? prev : [...prev, label]));
    if (openMenu !== label) {
      setOpenMenu(label);
      track("nav_mega_open", { label });
    }
  };

  return (
    <header
      className={cn(
        "fixed inset-x-0 z-50 px-4 transition-all duration-300",
        scrolled ? "top-2" : "top-4",
      )}
      onMouseLeave={scheduleClose}
    >
      {/* Backdrop overlay when mega open */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none fixed inset-0 -z-[1] bg-black/30 backdrop-blur-[2px] transition-opacity duration-200",
          openMenu ? "opacity-100" : "opacity-0",
        )}
      />

      <div
        className={cn(
          "relative mx-auto flex items-center justify-between rounded-full bg-white text-black ring-1 ring-black/5 transition-all duration-300",
          scrolled
            ? "max-w-6xl px-2 py-1.5 shadow-[0_14px_50px_-22px_rgba(0,0,0,0.55)] md:px-3"
            : "max-w-7xl px-3 py-2 shadow-[0_10px_40px_-22px_rgba(0,0,0,0.45)] md:px-4",
        )}
      >

        <Link
          to="/"
          aria-label={`Fenny — ${t("nav.home")}`}
          className="group flex items-center gap-2 rounded-full px-2.5 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/30"
        >
          <img src={symbol} alt="Fenny" aria-hidden className="h-5 w-auto md:hidden" />
          <img
            src={wordmark}
            alt="Fenny"
            className="hidden h-4 w-auto transition-transform duration-300 group-hover:scale-[1.02] md:block"
          />
        </Link>

        <nav className="hidden items-center gap-0.5 lg:flex" aria-label={t("nav.menu")}>
          {nav.map((entry) => {
            if (entry.kind === "link") {
              return (
                <Link
                  key={entry.to}
                  to={entry.to}
                  onMouseEnter={() => {
                    cancelClose();
                    setOpenMenu(null);
                  }}
                  className="rounded-full px-3.5 py-1.5 text-[14px] font-medium text-black/65 transition-colors duration-200 hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/30"
                  activeProps={{ className: "text-black bg-black/[0.06]" }}
                >
                  {entry.label}
                </Link>
              );
            }
            const isActive = entry.activeWhen.some(
              (p) => pathname === p || pathname.startsWith(p + "/"),
            );
            const isOpen = openMenu === entry.label;
            return (
              <button
                key={entry.label}
                type="button"
                onMouseEnter={() => handleOpen(entry.label)}
                // Pointern kan redan ligga över knappen när React hydrerar —
                // då hinner mouseenter aldrig avfyras och menyn känns "trasig".
                onMouseMove={() => handleOpen(entry.label)}
                onFocus={() => handleOpen(entry.label)}
                onClick={() => (isOpen ? setOpenMenu(null) : handleOpen(entry.label))}
                aria-haspopup="true"
                aria-expanded={isOpen}
                className={cn(
                  "flex items-center gap-1 rounded-full px-3.5 py-1.5 text-[14px] font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/30",
                  isActive || isOpen
                    ? "bg-black/[0.06] text-black"
                    : "text-black/65 hover:text-black",
                )}
              >
                {entry.label}
                <ChevronDown
                  className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")}
                />
              </button>
            );
          })}
          {comingSoonAreas(isEn ? "en" : "sv").map((area) => (
            <span
              key={area.id}
              className="ml-0.5 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[14px] font-medium text-black/35"
              title={area.note}
            >
              {area.label}
              <span className="rounded-full bg-black/[0.06] px-1.5 py-[2px] text-[9px] uppercase tracking-[0.14em] text-black/45">
                {area.note}
              </span>
            </span>
          ))}
        </nav>


        <div className="hidden items-center gap-1.5 lg:flex">
          <LanguageSwitcher variant="pill" />
          <Button
            to={contactTo as string}
            variant="peach"
            arrow
            size="sm"
            trackId="contact_us"
            trackProps={{ location: "header_desktop" }}
          >
            {t("nav.contact")}
          </Button>
        </div>

        <div className="flex items-center gap-1.5 lg:hidden">
          <LanguageSwitcher variant="pill" />
          <Link
            to={contactTo as string}
            onClick={() => track("cta_click", { id: "contact_us", location: "header_mobile_pill" })}
            className="inline-flex items-center gap-1 rounded-full bg-black px-3.5 py-1.5 text-[13px] font-semibold text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-black/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/30"
            aria-label={t("nav.contact")}
          >
            {t("nav.contact")}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <button
            className="-mr-1 inline-flex h-11 w-11 items-center justify-center rounded-full text-black transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/30"
            onClick={() => {
              setOpen((v) => !v);
              track(open ? "nav_mobile_close" : "nav_mobile_open", {});
            }}
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            <MorphIcon open={open} />
          </button>
        </div>
      </div>

      {/* Full-width mega panel */}
      <div
        className="relative mx-auto mt-3 hidden h-0 max-w-7xl lg:block"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      >
        {nav.map((entry) => {
          if (entry.kind === "link") return null;
          const isOpen = openMenu === entry.label;
          return (
            <div
              key={entry.label}
              className={cn(
                "absolute inset-x-0 top-0 origin-top transition-all duration-200 ease-out",
                isOpen
                  ? "pointer-events-auto translate-y-0 opacity-100"
                  : "pointer-events-none -translate-y-2 opacity-0",
              )}
              aria-hidden={!isOpen}
            >
              <div className="overflow-hidden rounded-3xl border border-black/5 bg-white text-black shadow-[0_30px_80px_-30px_rgba(0,0,0,0.45)]">
                {mountedMenus.includes(entry.label) && (
                  <MegaPanelContent panel={entry} onNav={() => setOpenMenu(null)} />
                )}
              </div>
            </div>
          );
        })}
      </div>


      {/* Premium mobile menu */}
      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </header>
  );
}

/**
 * Signature open/close icon — Fenny "F" morphs into a close stroke.
 * Pure CSS transforms, GPU-accelerated, respects reduced-motion via duration.
 */
function MorphIcon({ open }: { open: boolean }) {
  return (
    <span
      aria-hidden
      className="relative block h-5 w-5"
      style={{ contain: "layout paint" }}
    >
      {/* Top bar */}
      <span
        className={cn(
          "absolute left-0 top-[5px] h-[2px] w-5 origin-center rounded-full bg-black transition-all duration-[360ms] ease-[cubic-bezier(0.65,0,0.35,1)]",
          open && "top-[9px] rotate-45",
        )}
      />
      {/* Middle bar — becomes the vertical stem of F → fades on close X */}
      <span
        className={cn(
          "absolute left-0 top-[9px] h-[2px] rounded-full bg-black transition-all duration-[360ms] ease-[cubic-bezier(0.65,0,0.35,1)]",
          open ? "w-5 -rotate-45" : "w-3",
        )}
      />
      {/* Bottom bar — closes into the X, shifts to imply "F" foot when closed */}
      <span
        className={cn(
          "absolute left-0 h-[2px] origin-center rounded-full bg-black transition-all duration-[360ms] ease-[cubic-bezier(0.65,0,0.35,1)]",
          open ? "top-[9px] w-5 rotate-45 opacity-0" : "top-[13px] w-2.5",
        )}
      />
    </span>
  );
}
